import { useRef, useState, useEffect, useCallback } from "react";
import type { TextBox } from "@/types";

export function TextBoxCard({
  box,
  scaleRef,
  selected,
  autoFocus,
  onSelect,
  onMove,
  onSavePosition,
  onContentChange,
}: {
  box: TextBox;
  scaleRef: React.RefObject<number>;
  selected: boolean;
  autoFocus: boolean;
  onSelect: (id: string) => void;
  onMove: (id: string, x: number, y: number) => void;
  onSavePosition: (id: string, x: number, y: number) => void;
  onContentChange: (id: string, content: string) => void;
}) {
  const [editing, setEditing] = useState(autoFocus);
  const [lifted, setLifted] = useState(false);
  const dragging = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const boxRef = useRef(box);
  boxRef.current = box;
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const onSaveRef = useRef(onSavePosition);
  onSaveRef.current = onSavePosition;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const initialAutoFocus = useRef(autoFocus);

  const autoResize = useCallback(() => {
    const ta = textareaRef.current;
    const ms = measureRef.current;
    if (!ta || !ms) return;
    ms.textContent = ta.value || ta.placeholder || " ";
    ta.style.width = Math.max(80, ms.scrollWidth + 16) + "px";
    ta.style.height = "auto";
    ta.style.height = ta.scrollHeight + "px";
  }, []);

  useEffect(() => { autoResize(); }, [box.content, autoResize]);

  useEffect(() => {
    if (initialAutoFocus.current) {
      textareaRef.current?.focus();
      autoResize();
    }
  }, [autoResize]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (editing) return;
    e.stopPropagation();
    onSelect(box._id);
    dragging.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
    setLifted(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const s = scaleRef.current ?? 1;
    const dx = (e.clientX - lastMouse.current.x) / s;
    const dy = (e.clientY - lastMouse.current.y) / s;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    const b = boxRef.current;
    onMoveRef.current(b._id, b.x + dx, b.y + dy);
  };

  const onPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setLifted(false);
    const b = boxRef.current;
    onSaveRef.current(b._id, b.x, b.y);
  };

  return (
    <div
      style={{
        position: "absolute",
        left: box.x,
        top: box.y,
        display: "inline-block",
        zIndex: lifted ? 10 : 7,
        pointerEvents: "auto",
        outline: (selected || editing) ? "1.5px solid #0091ff" : "1.5px solid transparent",
        outlineOffset: 3,
        borderRadius: 3,
        filter: lifted ? "drop-shadow(0 8px 20px rgba(0,0,0,0.15))" : "none",
        transform: lifted ? "scale(1.02)" : "scale(1)",
        transition: "transform 0.1s ease-out",
        touchAction: "none",
        userSelect: editing ? "text" : "none",
        cursor: lifted ? "grabbing" : editing ? "text" : "grab",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onMouseDown={(e) => { e.stopPropagation(); onSelect(box._id); }}
      onMouseUp={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        setEditing(true);
        setTimeout(() => textareaRef.current?.focus(), 0);
      }}
    >
      <span
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          visibility: "hidden",
          whiteSpace: "pre",
          fontFamily: "inherit",
          fontSize: box.fontSize,
          lineHeight: 1.4,
          padding: "2px 8px",
          pointerEvents: "none",
        }}
      />
      <textarea
        ref={textareaRef}
        value={box.content}
        onChange={(e) => { onContentChange(box._id, e.target.value); autoResize(); }}
        placeholder="Type something..."
        rows={1}
        style={{
          display: "block",
          minWidth: 80,
          border: "none",
          background: "transparent",
          resize: "none",
          overflow: "hidden",
          outline: "none",
          fontFamily: "inherit",
          fontSize: box.fontSize,
          lineHeight: 1.4,
          color: "var(--foreground)",
          boxSizing: "border-box",
          padding: "2px 8px",
          cursor: "inherit",
          pointerEvents: editing ? "auto" : "none",
        }}
        onFocus={() => setEditing(true)}
        onBlur={() => setEditing(false)}
        onMouseDown={(e) => { if (editing) e.stopPropagation(); }}
        onMouseUp={(e) => e.stopPropagation()}
      />
    </div>
  );
}
